import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ActivityService } from "../../../services/user/activity.services";
import { handleNotify, TOASTER_POSITION, TOASTER_TYPE } from "../../common/notification/toaster_notify.component";
import DeleteModelPopupComponent from "../common_model_popup/deletemodelpopup.component";
import { handleCopy, handleExcel, handlePDF, handlePrint } from "../../../utils/filedownload";
import moment from "moment";
import DatePicker from "react-datepicker";
import { getButtonStyles, getDateFormat } from "../../../utility";

const ActivityComponent = () => {
    const navigate = useNavigate();
    const selectedStore = useSelector(state => state?.user?.selectedStoreId);
    const [searchText, setSearchText] = useState("");
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [showModal, setShowModal] = useState(false);
    const [activityToDelete, setActivityToDelete] = useState(null);
    const [isFilterMode, setIsFilterMode] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState("");
    const [filterType, setFilterType] = useState("Follow-up");
    const [fromDate, setFromDate] = useState(moment());
    const [toDate, setToDate] = useState(moment());
    const [selectedStatusCard, setSelectedStatusCard] = useState(null);
    const rowsPerPage = 5;

    // Fetch data
    useEffect(() => {
        const fetchActivityData = async () => {
            try {
                setLoading(true);
                const response = await ActivityService.getAllActivities(selectedStore);
                setData(response);
            } catch (error) {
                handleNotify("Error fetching activities", TOASTER_TYPE.ERROR, TOASTER_POSITION.TOP_RIGHT);
                setData([]);
            } finally {
                setLoading(false);
            }
        };
        fetchActivityData();
    }, [selectedStore]);

    const filterData = (data) => {
        return (
            data.filter(row =>
                (selectedStatusCard ? row.status?.statusName === selectedStatusCard : true) &&
                (
                    row.leads?.companyName?.toLowerCase().includes(searchText.toLowerCase()) ||
                    row.activitySubject?.toLowerCase().includes(searchText.toLowerCase()) ||
                    row.relatedTo?.toLowerCase().includes(searchText.toLowerCase()) ||
                    row.user?.username?.toLowerCase().includes(searchText.toLowerCase()) ||
                    row.priority?.priorityName.toLowerCase().includes(searchText.toLowerCase()) ||
                    row.activityDate.toLowerCase().includes(searchText.toLowerCase()) ||
                    (row.status?.statusName?.toLowerCase().includes(searchText.toLowerCase()))
                )
            )
        )
    };

    //handle pagenation 
    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const filteredData = filterData(data);
    const currentData = filteredData.slice(indexOfFirstRow, indexOfLastRow);
    const totalPages = Math.ceil(filteredData.length / rowsPerPage);

    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
    };

    // Handle Edit
    const handleEditClick = (item) => {
        navigate('/activity/edit', { state: { activityId: item?.activityId } });
    };

    // Handle Delete
    const handleDeleteClick = (activity) => {
        setActivityToDelete(activity);
        setShowModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (activityToDelete) {
            try {
                await ActivityService.deleteActivity(activityToDelete.activityId);

                const newData = data.filter(activity => activity.activityId !== activityToDelete.activityId);
                setData(newData);

                const newFilteredData = filterData(newData);

                const newTotalPages = Math.ceil(newFilteredData.length / rowsPerPage);

                if (newFilteredData.length % rowsPerPage === 0 && currentPage > 1) {
                    setCurrentPage(currentPage - 1);
                } else if (currentPage > newTotalPages && newTotalPages > 0) {
                    setCurrentPage(newTotalPages);
                }

                setShowModal(false);
            } catch (error) {
                handleNotify(error.message || "Error while deleting Activity.", TOASTER_TYPE.ERROR, TOASTER_POSITION.TOP_RIGHT);
            }
        }
    };

    const handleAddNewClick = () => {
        navigate('/activity/create');
    };

    const handleRefreshClick = () => {
        setIsFilterMode(false);
        setSelectedStatus("");
        setFromDate(moment());
        setToDate(moment());
    };

    // filter Button functionality
    const handleFilterButtonChanges = (e) => {
        const { name, value } = e.target;
        if (name === "type") {
            setFilterType(value);
        }
        if (name === "fromDate") {
            setFromDate(value);
        }
        if (name === "toDate") {
            setToDate(value);
        }
    };

    const filteredDataBtn = data.filter(row => {
        const matchesType = filterType === "Follow-up"
            ? row.followUp === true
            : filterType === "Demo"
                ? row.isDemoRequired === true
                : true;

        const activityDate = moment(row.activityDate, 'DD/MM/YYYY');

        const isWithinDateRange = activityDate.isBetween(fromDate, toDate, undefined, '[]');

        return matchesType && isWithinDateRange;
    });

    // Handle Status Filter Click
    const handleStatusFilterClick = (statusName) => {
        setSelectedStatus(statusName);
    };

    // Calculate data by status
    const ActivityByStatus = data.reduce((acc, activity) => {
        const { statusName } = activity.status || { statusName: 'No Requirement' };
        if (!acc[statusName]) {
            acc[statusName] = { count: 0 };
        }
        acc[statusName].count += 1;
        return acc;
    }, {});

    const totalActivities = data.length;

    // Calculate percentage
    const getStatusPercentage = (statusCount) => {
        return totalActivities > 0 ? ((statusCount / totalActivities) * 100).toFixed(2) : 0;
    };
    // Filter data by selected status
    const filteredDataByStatus = data.filter(row => row.status?.statusName === selectedStatus);

    const handleFilterClick = () => {
        if (!isFilterMode) {
            setIsFilterMode(true);
            setCurrentPage(1);
        }
    };

    // Columns for DataTable
    const columns = [
        {
            name: "Reference Name",
            selector: (row) => row.leads?.companyName,
            sortable: true,
            width: '15%',
        },
        {
            name: "Subject",
            selector: (row) => row.activitySubject,
            sortable: true,
            width: '15%',
        },
        {
            name: "Type",
            selector: (row) => row.relatedTo,
            sortable: true,
            width: '10%',
        },
        {
            name: "Priority",
            selector: (row) => row.priority?.priorityName,
            sortable: true,
            width: '10%',
        },
        {
            name: "Status",
            selector: (row) => row.status?.statusName,
            sortable: true,
            width: '10%',
            cell: (row) => {
                const { backgroundColor, textColor } = getButtonStyles(row.status);

                return (
                    <div className="d-flex align-items-center justify-content-between">
                        <button
                            className="btn btn-sm"
                            style={{
                                padding: "6px 12px",
                                fontSize: "14px",
                                borderRadius: "25px",
                                backgroundColor: backgroundColor,
                                color: textColor,
                            }}
                        >
                            {row.status?.statusName}
                        </button>
                    </div>
                );
            },
        },
        {
            name: 'Created',
            width: '10%',
            selector: row => row.activityDate || '',
            sortable: true
        },
        {
            name: "Follow-up",
            selector: (row) => row.followUp,
            sortable: true,
            width: '10%',
            cell: row => (
                <button className={`btn btn-light radius-30 px-4 ${row.followUp ? 'text-success' : 'text-danger'}`}>
                    {row.followUp ? "Yes" : "No"}
                </button>
            ),
        },
        {
            name: "Buyback?",
            selector: (row) => row.buyback,
            sortable: true,
            width: '10%',
            cell: row => (
                <button className={`btn btn-light radius-30 px-4 ${row.buyback ? 'text-success' : 'text-danger'}`}>
                    {row.buyback ? "Yes" : "No"}
                </button>
            ),
        },
        {
            name: "Demo Required?",
            selector: (row) => row.isDemoRequired,
            sortable: true,
            width: '10%',
            cell: row => (
                <button className={`btn btn-light radius-30 px-4 ${row.isDemoRequired ? 'text-success' : 'text-danger'}`}>
                    {row.isDemoRequired ? "Yes" : "No"}
                </button>
            ),
        },
        {
            name: "Assigned To",
            selector: (row) => {
                return row.user?.username && row.user?.role?.roleName
                    ? `${row.user.username} (${row.user.role.roleName})`
                    : "";
            },
            sortable: true,
            width: '15%',
        },
        {
            name: "Updated By",
            selector: (row) => {
                return row.user?.username && row.user?.role?.roleName
                    ? `${row.user.username} (${row.user.role.roleName})`
                    : "";
            },
            sortable: true,
            width: '15%',
        },
        {
            name: "Actions",
            button: true,
            width: "10%",
            cell: (row) => (
                <div className="d-flex justify-content-center gap-2">
                    <button className="btn btn-primary" onClick={() => handleEditClick(row)}>
                        Edit
                    </button>
                    <button className="btn btn-danger" onClick={() => handleDeleteClick(row)}>
                        Delete
                    </button>
                </div>
            ),
        },
    ];

    const handleDateChange = (val, name) => {
        if (name === "fromDate") setFromDate(moment(val));
        if (name === "toDate") setToDate(moment(val));
    };


    return (
        <div className="page-wrapper m-0">
            <div className="page-content">
                <div className="page-breadcrumb d-flex align-items-center mb-3">
                    <div className="breadcrumb-title pe-3 text-primary fw-bold fs-4">
                        {selectedStatus ? `${selectedStatus} Activity` : (isFilterMode ? "Follow up Activity" : "Activities")}
                    </div>
                    <div className="ms-auto btn-group">
                        <button className="btn btn-secondary" onClick={handleRefreshClick}>
                            Refresh
                        </button>
                        <button className="btn btn-primary" onClick={handleAddNewClick}>
                            New +
                        </button>
                    </div>
                </div>
                {/* Status Filters */}
                <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 g-3 mt-1">
                    {Object.keys(ActivityByStatus).map((statusName) => {
                        const { count } = ActivityByStatus[statusName];
                        const percentage = getStatusPercentage(count);
                        const { backgroundColor } = getButtonStyles({
                            colorId: data.find(activity => activity.status?.statusName === statusName)?.status?.colorId
                        });

                        return (
                            <div key={statusName} className="col-12 col-sm-6 col-lg-2" onClick={() => handleStatusFilterClick(statusName)}>
                                <div className="card radius-10" style={{ borderLeft: `4px solid ${backgroundColor}` }}>
                                    <div className="card-body text-center">
                                        <h5 className="d-flex" style={{ color: backgroundColor }}>
                                            {statusName}
                                        </h5>

                                        <h5 className="d-flex">{count}</h5>
                                        <div className="d-flex justify-content-between align-items-center">
                                            <p className="mb-0 font-13 text-primary">{percentage}%</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
                {/* Filter Section */}
                <div className="row row-cols-1 row-cols-md-4 g-4 mt-3">
                    <div className="col">
                        <div className="form-group">
                            <label htmlFor="filterType" className="form-label d-flex">Filter By:</label>
                            <select
                                id="filterType"
                                name="type"
                                className="form-select"
                                onChange={handleFilterButtonChanges}
                                value={filterType}
                            >
                                <option value="Follow-up">Follow-up</option>
                                <option value="Demo">Demo</option>
                            </select>
                        </div>
                    </div>
                    <div className="col">
                        <div className="form-group">
                            <label htmlFor="fromDate" className="form-label d-flex">From Date:</label>
                            <div className="d-flex">
                                <DatePicker
                                    selected={moment(fromDate, "DD/MM/YYYY").toDate()}
                                    onChange={(date) => handleDateChange(date, "fromDate")}
                                    dateFormat={getDateFormat()}
                                    className="form-control"
                                    portalId="root"
                                />
                            </div>
                        </div>
                    </div>
                    <div className="col">
                        <div className="form-group">
                            <label htmlFor="toDate" className="form-label d-flex">To Date</label>
                            <div className="d-flex">
                                <DatePicker
                                    selected={moment(toDate, "DD/MM/YYYY").toDate()}
                                    onChange={(date) => handleDateChange(date, "toDate")}
                                    dateFormat={getDateFormat()}
                                    className="form-control"
                                    portalId="root"
                                />
                            </div>
                        </div>
                    </div>
                    <div className="col d-flex justify-content-end align-items-center mt-5">
                        <button
                            className="btn btn-primary w-50 ms-5"
                            onClick={handleFilterClick}
                        >
                            Filter
                        </button>
                    </div>
                </div>
                <div className="card radius-10 border-top border-0 border-4 border-primary mt-3 p-4">
                    <div className="card-body">
                        <div className="d-flex flex-column flex-md-row mb-3">
                            <div className="btn-group mb-3 mb-md-0 me-2">
                                <button className="btn btn-outline-secondary" onClick={() => handleCopy(filteredData)}>Copy</button>
                                <button className="btn btn-outline-secondary" onClick={() => handleExcel(filteredData)}>Excel</button>
                                <button className="btn btn-outline-secondary" onClick={() => handlePDF(filteredData)}>PDF</button>
                                <button className="btn btn-outline-secondary" onClick={handlePrint}>Print</button>
                            </div>
                            <div className="d-flex ms-auto align-items-center">
                                <div className="me-2">
                                    <span className="fw-bold">Search:</span>
                                </div>
                                <input
                                    type="text"
                                    className="form-control w-100 w-md-auto"
                                    placeholder="Search 🔍..."
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="table-responsive">
                            <DataTable
                                columns={columns}
                                data={selectedStatus ? filteredDataByStatus : (isFilterMode ? filteredDataBtn : currentData)}
                                fixedHeader
                                responsive
                                highlightOnHover
                                sortIcon={<i className="fadeIn animated bx bx-sort" />}
                                progressPending={loading}
                            />
                        </div>
                        {showModal && (
                            <DeleteModelPopupComponent
                                show={showModal}
                                onClose={() => setShowModal(false)}
                                onConfirm={handleDeleteConfirm}
                                title="Confirm"
                                confirmText="Delete"
                                Message="Are you sure you want to delete this activity?"
                            />
                        )}
                        {/* Pagination Controls */}
                        <div className="d-flex mt-4 justify-content-end">
                            <div className="btn-group">
                                <button
                                    className="btn btn-outline-secondary"
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    Prev
                                </button>
                                {Array.from({ length: totalPages }, (_, index) => (
                                    <button
                                        key={index}
                                        className={`btn ${currentPage === index + 1 ? 'btn-primary' : 'btn-outline-secondary'}`}
                                        onClick={() => handlePageChange(index + 1)}
                                    >
                                        {index + 1}
                                    </button>
                                ))}
                                <button
                                    className="btn btn-outline-secondary"
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
export default ActivityComponent;
